import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import type { IDCComparisons } from '@popety-io/types'
import { MetricCard } from '../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../TooltipMetricExplanation'
import { IDCGaugeChart } from './components/IDCGaugeChart'
import { IDCRegulatoryBanner } from './components/IDCRegulatoryBanner'
import { IDCComparisonCard } from './components/IDCComparisonCard'
import {
  getIDCStatus,
  convertMJtoKWh,
  IDC_BASE_THRESHOLD,
} from './utils/idcCalculations'

interface IDCTabProps {
  buildingId: string
  idc: number // MJ/m²·year
  buildingYear?: number
  t: TFunction
  onFetchIDCComparisons: (buildingId: string) => Promise<IDCComparisons>
}

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`

const InfoBanner = styled.div`
  background: linear-gradient(135deg, #3b82f615 0%, #3b82f608 100%);
  border: 1px solid #3b82f630;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: 16px 20px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;

  strong {
    color: #3b82f6;
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }
`

const DataSourceNote = styled.div`
  margin-top: 32px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const IDCTab: React.FC<IDCTabProps> = ({
  buildingId,
  idc,
  buildingYear,
  t,
  onFetchIDCComparisons,
}) => {
  const currentYear = new Date().getFullYear()
  const status = getIDCStatus(idc, currentYear)

  // Calculate position display
  const exceedanceDisplay =
    status.exceedancePercent > 0
      ? `+${Math.round(status.exceedancePercent)}%`
      : `${Math.round(status.exceedancePercent)}%`

  // Determine variant for position metric based on status
  const getPositionVariant = ():
    | 'success'
    | 'warning'
    | 'danger'
    | 'default' => {
    if (status.exceedancePercent <= 0) return 'success'
    if (status.status === 'critical') return 'danger'
    if (status.status === 'warning') return 'warning'
    return 'default'
  }

  // Get period text based on current year
  const getPeriodText = () => {
    if (currentYear <= 2026)
      return t(
        'idcTab.metrics.significantExceedanceThreshold.periods.until2026'
      )
    if (currentYear <= 2030)
      return t(
        'idcTab.metrics.significantExceedanceThreshold.periods.until2030'
      )
    return t('idcTab.metrics.significantExceedanceThreshold.periods.from2031')
  }

  return (
    <TabContainer>
      {/* Information banner */}
      <InfoBanner>{t('idcTab.infoBanner')}</InfoBanner>

      {/* Metrics Grid with 3 cards */}
      <MetricsGrid>
        <MetricCard
          title={t('idcTab.metrics.regulatoryThreshold.title')}
          value={`${IDC_BASE_THRESHOLD} MJ/m²`}
          subtitle={`${Math.round(convertMJtoKWh(IDC_BASE_THRESHOLD))} kWh/m²·an`}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('idcTab.metrics.regulatoryThreshold.title')}
              shortDescription={t(
                'energy.idcTab.metrics.regulatoryThreshold.tooltip.description'
              )}
              calculation={t(
                'energy.idcTab.metrics.regulatoryThreshold.tooltip.calculation',
                {
                  threshold: IDC_BASE_THRESHOLD,
                  kWh: Math.round(convertMJtoKWh(IDC_BASE_THRESHOLD)),
                }
              )}
              importance={t(
                'energy.idcTab.metrics.regulatoryThreshold.tooltip.importance'
              )}
              source={t(
                'energy.idcTab.metrics.regulatoryThreshold.tooltip.source'
              )}
            />
          }
        />

        <MetricCard
          title={t('idcTab.metrics.yourPosition.title')}
          value={exceedanceDisplay}
          subtitle={`IDC: ${Math.round(idc)} MJ/m²`}
          variant={getPositionVariant()}
          tooltip={
            <TooltipMetricExplanation
              title={t('idcTab.metrics.yourPosition.title')}
              shortDescription={t(
                'energy.idcTab.metrics.yourPosition.tooltip.description',
                {
                  threshold: IDC_BASE_THRESHOLD,
                }
              )}
              calculation={t(
                'energy.idcTab.metrics.yourPosition.tooltip.calculation'
              )}
              importance={t(
                'energy.idcTab.metrics.yourPosition.tooltip.importance'
              )}
              source={t('idcTab.metrics.yourPosition.tooltip.source')}
            />
          }
        />

        <MetricCard
          title={t(
            'energy.idcTab.metrics.significantExceedanceThreshold.title'
          )}
          value={`${status.significantThreshold} MJ/m²`}
          subtitle={
            idc > status.significantThreshold
              ? t(
                  'energy.idcTab.metrics.significantExceedanceThreshold.exceeded'
                )
              : t(
                  'energy.idcTab.metrics.significantExceedanceThreshold.notExceeded'
                )
          }
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t(
                'energy.idcTab.metrics.significantExceedanceThreshold.title'
              )}
              shortDescription={t(
                'energy.idcTab.metrics.significantExceedanceThreshold.tooltip.description'
              )}
              calculation={t(
                'energy.idcTab.metrics.significantExceedanceThreshold.tooltip.calculation',
                {
                  threshold: status.significantThreshold,
                  period: getPeriodText(),
                }
              )}
              importance={t(
                'energy.idcTab.metrics.significantExceedanceThreshold.tooltip.importance'
              )}
              limitations={t(
                'energy.idcTab.metrics.significantExceedanceThreshold.tooltip.limitations'
              )}
              source={t(
                'energy.idcTab.metrics.significantExceedanceThreshold.tooltip.source'
              )}
            />
          }
        />
      </MetricsGrid>

      {/* Gauge chart with regulatory banner + Comparison card */}
      <TwoColumnGrid>
        <LeftColumn>
          <IDCGaugeChart idc={idc} currentYear={currentYear} t={t} />
          <IDCRegulatoryBanner idc={idc} currentYear={currentYear} t={t} />
        </LeftColumn>
        <IDCComparisonCard
          buildingId={buildingId}
          idc={idc}
          buildingYear={buildingYear}
          t={t}
          onFetchComparisons={onFetchIDCComparisons}
        />
      </TwoColumnGrid>

      {/* Data source attribution */}
      <DataSourceNote>
        <strong>{t('idcTab.dataSourceNote.title')}</strong>{' '}
        {t('idcTab.dataSourceNote.description')}{' '}
        <a
          href="https://www.ge.ch/dossier/optimiser-consommation-energie-son-batiment/determiner-indice-depense-chaleur-idc"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('idcTab.dataSourceNote.linkText')}
        </a>
      </DataSourceNote>
    </TabContainer>
  )
}
